import React from 'react'
import { useCssHandles } from '../hooks/useCssHandles'
import { Link } from 'react-router-dom'
import Input from './Input'

const CSS_HANDLES = [
  'footer',
  'footerContainer',
  'footerContent',
  'footerSection',
  'footerTitle',
  'footerList',
  'footerItem',
  'footerLink',
  'footerBottom',
  'footerCopyright',
  'footerSocial',
  'socialList',
  'socialItem',
  'socialLink',
  'socialIcon',
  'footerLogo',
  'footerNewsletter',
  'newsletterForm',
  'newsletterInput',
  'newsletterButton',
  'contactItem',
  'contactIcon',
  'contactText',
  'footerHelpCta',
  'footerHelpCtaContent',
  'footerHelpCtaTitle',
  'footerHelpCtaDescription',
  'footerHelpCtaButton'
] as const

const Footer: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)

  return (
    <footer className={`${handles.footer} bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white relative overflow-hidden`}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FFD700' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>
      
      <div className={`${handles.footerContainer} max-w-7xl mx-auto px-6 py-16 relative z-10`}>
        <div className={`${handles.footerContent} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12`}>
          
          {/* Sobre a Empresa */}
          <div className={`${handles.footerSection} lg:col-span-1`}>
            <div className="mb-6">
              <img 
                src="/assets/logo-branco-sem-slogan.svg" 
                alt="Logo da Norte Sul Baterias" 
                className={`${handles.footerLogo} h-16 w-auto`} 
              />
            </div>
            <p className="text-slate-300 mb-6 leading-relaxed text-sm">
              Especialistas em baterias automotivas com mais de 10 anos de experiência. 
              Qualidade garantida e os melhores preços do mercado.
            </p>
            
            {/* Newsletter */}
            <div className={`${handles.footerNewsletter} mb-6`}>
              <h4 className="text-yellow-400 font-semibold mb-3 text-sm">Receba nossas ofertas</h4>
              <form className={`${handles.newsletterForm} flex gap-2`}>
                <Input 
                  type="email" 
                  placeholder="Seu e-mail" 
                  className={`${handles.newsletterInput} flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all`}
                />
                <button 
                  type="submit"
                  className={`${handles.newsletterButton} px-4 py-2 bg-yellow-400 text-slate-900 font-semibold rounded-md hover:bg-yellow-300 transition-colors text-sm`}
                >
                  OK
                </button>
              </form>
            </div>

            {/* Social Media */}
            <div className={`${handles.footerSocial}`}>
              <h4 className="text-yellow-400 font-semibold mb-3 text-sm">Siga-nos</h4>
              <ul className={`${handles.socialList} flex space-x-3`}>
                <li className={`${handles.socialItem}`}>
                  <a 
                    href="https://www.instagram.com/bateriasbaterpower/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className={`${handles.socialLink} w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center text-slate-300 hover:bg-yellow-400 hover:text-slate-900 transition-all duration-300 transform hover:scale-110`}
                    aria-label="Instagram"
                  >
                    <svg className={`${handles.socialIcon} w-4 h-4`} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Links Rápidos */}
          <div className={`${handles.footerSection}`}>
            <h3 className={`${handles.footerTitle} text-lg font-bold mb-6 text-yellow-400 relative`}>
              Links Rápidos
              <div className="absolute -bottom-2 left-0 w-8 h-0.5 bg-yellow-400"></div>
            </h3>
            <ul className={`${handles.footerList} space-y-3`}>
              <li className={`${handles.footerItem}`}>
                <Link 
                  to="/baterias" 
                  className={`${handles.footerLink} text-slate-300 hover:text-yellow-400 transition-all duration-300 flex items-center group text-sm`}
                >
                  <span className="w-1 h-1 bg-yellow-400 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  Todas as Baterias
                </Link>
              </li>
              <li className={`${handles.footerItem}`}>
                <Link 
                  to="/marcas" 
                  className={`${handles.footerLink} text-slate-300 hover:text-yellow-400 transition-all duration-300 flex items-center group text-sm`}
                >
                  <span className="w-1 h-1 bg-yellow-400 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  Marcas
                </Link>
              </li>
              <li className={`${handles.footerItem}`}>
                <Link 
                  to="/catalogo" 
                  className={`${handles.footerLink} text-slate-300 hover:text-yellow-400 transition-all duration-300 flex items-center group text-sm`}
                >
                  <span className="w-1 h-1 bg-yellow-400 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  Ofertas
                </Link>
              </li>
            </ul>
          </div>

          {/* Suporte */}
          <div className={`${handles.footerSection}`}>
            <h3 className={`${handles.footerTitle} text-lg font-bold mb-6 text-yellow-400 relative`}>
              Suporte
              <div className="absolute -bottom-2 left-0 w-8 h-0.5 bg-yellow-400"></div>
            </h3>
            <ul className={`${handles.footerList} space-y-3`}>
              <li className={`${handles.footerItem}`}>
                <Link 
                  to="/sobre" 
                  className={`${handles.footerLink} text-slate-300 hover:text-yellow-400 transition-all duration-300 flex items-center group text-sm`}
                >
                  <span className="w-1 h-1 bg-yellow-400 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  Contato
                </Link>
              </li>
              <li className={`${handles.footerItem}`}>
                <Link 
                  to="/ajuda" 
                  className={`${handles.footerLink} text-slate-300 hover:text-yellow-400 transition-all duration-300 flex items-center group text-sm`}
                >
                  <span className="w-1 h-1 bg-yellow-400 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  Central de Ajuda
                </Link>
              </li>
              <li className={`${handles.footerItem}`}>
                <Link 
                  to="/trocas" 
                  className={`${handles.footerLink} text-slate-300 hover:text-yellow-400 transition-all duration-300 flex items-center group text-sm`}
                >
                  <span className="w-1 h-1 bg-yellow-400 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  Trocas e Devoluções
                </Link>
              </li>
              <li className={`${handles.footerItem}`}>
                <Link 
                  to="/ajuda" 
                  className={`${handles.footerLink} text-slate-300 hover:text-yellow-400 transition-all duration-300 flex items-center group text-sm`}
                >
                  <span className="w-1 h-1 bg-yellow-400 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  Garantia
                </Link>
              </li>
            </ul>
          </div>

          {/* Contato */}
          <div className={`${handles.footerSection}`}>
            <h3 className={`${handles.footerTitle} text-lg font-bold mb-6 text-yellow-400 relative`}>
              Contato
              <div className="absolute -bottom-2 left-0 w-8 h-0.5 bg-yellow-400"></div>
            </h3>
            <div className="space-y-4">
              <div className={`${handles.contactItem} flex items-start space-x-3`}>
                <div className={`${handles.contactIcon} w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0`}>
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                  </svg>
                </div>
                <p className={`${handles.contactText} text-slate-300 text-sm`}>
                  (19) 3255-2356
                </p>
              </div>
              
              <div className={`${handles.contactItem} flex items-start space-x-3`}>
                <div className={`${handles.contactIcon} w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0`}>
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                  </svg>
                </div>
                <p className={`${handles.contactText} text-slate-300 text-sm`}>
                  contato@baterpower.com.br
                </p>
              </div>
              
              <div className={`${handles.contactItem} flex items-start space-x-3`}>
                <div className={`${handles.contactIcon} w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0`}>
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                  </svg>
                </div>
                <p className={`${handles.contactText} text-slate-300 text-sm leading-relaxed`}>
                  Av. José de Sousa Campos, 2086<br />
                  Nova Campinas, Campinas - SP<br />
                  CEP: 13025-320
                </p>
              </div>
              
              <div className={`${handles.contactItem} flex items-start space-x-3`}>
                <div className={`${handles.contactIcon} w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0`}>
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                  </svg>
                </div>
                <p className={`${handles.contactText} text-slate-300 text-sm leading-relaxed`}>
                  Seg-Sex: 8h às 18h<br />
                  Sáb: 8h às 14h
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Help Center CTA */}
        <div className={`${handles.footerHelpCta} mt-16`}>
          <div className={`${handles.footerHelpCtaContent} bg-white/10 backdrop-blur-sm border border-white/10 rounded-3xl px-8 py-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 shadow-2xl`}>
            <div>
              <h3 className={`${handles.footerHelpCtaTitle} text-2xl font-semibold text-yellow-300 mb-2`}>
                Precisa de suporte agora?
              </h3>
              <p className={`${handles.footerHelpCtaDescription} text-slate-200`}>
                Acesse a Central de Ajuda e veja como falar com nossa equipe pelo WhatsApp em poucos passos.
              </p>
            </div>
            <Link
              to="/ajuda"
              className={`${handles.footerHelpCtaButton} inline-flex items-center justify-center px-6 py-3 rounded-xl bg-yellow-400 text-slate-900 font-semibold shadow-lg hover:bg-yellow-300 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-200 focus:ring-offset-slate-900`}
            >
              Ir para a Central de Ajuda
            </Link>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className={`${handles.footerBottom} border-t border-slate-700 mt-12 pt-8`}>
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
            <div className={`${handles.footerCopyright} text-slate-400 text-sm text-center lg:text-left`}>
              © 2024 Baterpower Baterias. Todos os direitos reservados.
            </div>
            <div className="flex flex-wrap justify-center lg:justify-end space-x-6 gap-y-2">
              <Link 
                to="/privacidade" 
                className="text-slate-400 hover:text-yellow-400 text-sm transition-colors duration-300"
              >
                Política de Privacidade
              </Link>
              <Link 
                to="/termos" 
                className="text-slate-400 hover:text-yellow-400 text-sm transition-colors duration-300"
              >
                Termos de Uso
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer

